import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { SessionTimeoutService, SessionWarning } from 'src/app/services/session-timeout.service';

/**
 * Replaces the browser's native "You will be signed out" alert with an in-app dialog:
 * a countdown to the session deadline, "Stay signed in" (one authenticated request,
 * which refreshes the server clock) and "Sign out". Mounted once in the app shell and
 * driven entirely by SessionTimeoutService.warning$.
 */
@Component({
  selector: 'app-session-timeout-dialog',
  templateUrl: './session-timeout-dialog.component.html',
  styleUrls: ['./session-timeout-dialog.component.css'],
})
export class SessionTimeoutDialogComponent implements OnInit, OnDestroy {
  private sessionTimeout = inject(SessionTimeoutService);

  @ViewChild('stayBtn') private stayBtn?: ElementRef<HTMLButtonElement>;

  warning: SessionWarning | null = null;
  secondsLeft = 0;
  busy = false;
  /** Set when "Stay signed in" could not reach the server; cleared on the next attempt. */
  error = false;
  private previouslyFocused: Element | null = null;

  private warningSub: Subscription | null = null;
  private tickSub: Subscription | null = null;

  ngOnInit(): void {
    this.warningSub = this.sessionTimeout.warning$.subscribe((w) => {
      const opening = !!w && !this.warning;
      this.warning = w;
      this.busy = false;
      this.error = false;
      this.stopTicking();
      if (w) {
        this.updateSeconds(w.deadline);
        this.tickSub = interval(1000).subscribe(() => this.updateSeconds(w.deadline));
        if (opening) {
          this.previouslyFocused = document.activeElement;
          // The button exists after the next change detection; focus it then.
          setTimeout(() => this.stayBtn?.nativeElement.focus(), 0);
        }
      } else if (this.previouslyFocused instanceof HTMLElement) {
        this.previouslyFocused.focus();
        this.previouslyFocused = null;
      }
    });
  }

  ngOnDestroy(): void {
    this.warningSub?.unsubscribe();
    this.stopTicking();
  }

  /** m:ss for the dialog; never below 0:00. */
  get countdown(): string {
    const m = Math.floor(this.secondsLeft / 60);
    const s = this.secondsLeft % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  async stay(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.error = false;
    try {
      await this.sessionTimeout.keepAlive();
      // Success reschedules the timers and clears warning$, which hides the dialog.
    } catch {
      // The server could not be reached: keep the dialog and the countdown, say so.
      this.error = true;
    } finally {
      this.busy = false;
    }
  }

  async signOut(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.sessionTimeout.signOutNow();
    } catch {
      this.busy = false;
    }
  }

  private updateSeconds(deadline: number): void {
    this.secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
  }

  private stopTicking(): void {
    this.tickSub?.unsubscribe();
    this.tickSub = null;
  }
}
